import _, {
  chain,
  compact,
  flow,
  identity,
  invert,
  isEqual,
  isUndefined,
  keyBy,
  map,
  partition,
  union,
  uniq,
} from 'lodash';
import {DateTime} from 'luxon';

import {isDefined} from '../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {ChannelModel} from '../../../libs/shared/core/src/models/api/channelModel';
import {MessageTypesEnum} from '../../../libs/shared/core/src/models/api/commonMessageModel';
import {BumpStrategiesEnum} from '../../../libs/shared/core/src/models/api/companyPreferencesModel';
import {
  BumpedAtTimestamp,
  ConversationActionContextsEnum,
  ConversationId,
  ConversationSortPropertiesEnum,
  ConversationTeamStatusesEnum,
} from '../../../libs/shared/core/src/models/api/conversationCommonTypes';
import {
  ConversationLiveTrackerModel,
  ConversationTrackerModel,
  ConversationTrackerStagesEnum,
  ConversationTrackerStatusesEnum,
  isLiveTracker,
} from '../../../libs/shared/core/src/models/api/conversationTrackerModel';
import {InboxModel} from '../../../libs/shared/core/src/models/api/inboxModel';
import {MessageModel, MessageStatusesEnum} from '../../../libs/shared/core/src/models/api/messageModel';
import {
  isTeammateNamespace,
  isTeamNamespace,
} from '../../../libs/shared/core/src/models/helpers/namespaceHelpers';
import {reportToBugsnag} from '../../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  CellsContext,
  CellsContextTypesEnum,
} from '../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {
  findRelativeTimeDistance,
  TimeDistanceFormatsEnum,
} from '../../../libs/shared/ui/src/components/datetime/relativeDateTimeFormatter';
import {ActivityModel, ActivityVisualTypesEnum, BasicActivityTypesEnum} from '../../models/api/activityModel';
import {
  ContentUpdatedDates,
  ConversationBaseModel,
  ConversationLastComment,
  ConversationLastMessage,
  ConversationModel,
} from '../../models/api/conversationModel';
import {DataGroupModel} from '../../models/api/dataGroupModel';
import {VisualRecipientModel} from '../../models/api/visualRecipientModel';
import {buildPublicId} from '../../models/applications/publicIdTypes';
import {
  computeIsAutoResponseMessageActivitySource,
  computeMessageActivitySourceVisualType,
} from '../../store/data/activities/autoResponseMessageActivityHelpers';
import {
  InboxesConversationsParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
} from '../../store/panels/inboxes/parameters/inboxesParameters';
import {
  CellsSortModesEnum,
  findParametersCellsSortMode,
} from '../../store/panels/inboxes/parameters/inboxesParametersHelpers';
import {buildTeammateNamespace, findTrackerUnsnoozedDate, isTrackerUnsnoozed} from '../modelHelpers';
import {getEarliestSlaBreach} from './ruleBundleHelpers';

/*
 * Queries.
 */

type AssignedConversationModel = ConversationModel & {assigneeId: number};

export function isConversationAssigned(
  conversation: ConversationBaseModel | undefined,
): conversation is AssignedConversationModel {
  return !isUndefined(conversation?.assigneeId);
}

/*
 * Computed properties.
 */

export function buildConversationPublicId(conversation: ConversationBaseModel) {
  return buildPublicId('conversation', conversation.id);
}

export function isTeammateConversation({namespace}: ConversationBaseModel) {
  return isTeammateNamespace(namespace) || namespace === 'global';
}

export function isInternalConversation({messageType}: ConversationBaseModel) {
  return messageType === MessageTypesEnum.INTERNAL;
}

export function isFrontChatConversation({messageType}: ConversationModel) {
  return messageType === MessageTypesEnum.FRONT_CHAT;
}

/*
 * Merge.
 */

export enum ConversationsMergeCheckErrorsEnum {
  SINGLE_CONVERSATION = 'SINGLE_CONVERSATION',
  MESSAGE_TYPE_MISMATCH = 'MESSAGE_TYPE_MISMATCH',
  UNSUPPORTED_CHANNEL = 'UNSUPPORTED_CHANNEL',
  NONE = 'NONE',
}

export function computeConversationsMergeError(
  conversations: ReadonlyArray<ConversationBaseModel>,
): ConversationsMergeCheckErrorsEnum {
  // At least two conversations.
  if (conversations.length < 2) {
    return ConversationsMergeCheckErrorsEnum.SINGLE_CONVERSATION;
  }

  // Same message type.
  const messageTypes = _(conversations)
    .map((c) => c.messageType)
    .uniq()
    .value();

  if (messageTypes.length > 1) {
    return ConversationsMergeCheckErrorsEnum.MESSAGE_TYPE_MISMATCH;
  }

  // Supported message type.
  const messageType = messageTypes[0];
  if (messageType !== MessageTypesEnum.EMAIL && messageType !== MessageTypesEnum.TWEET) {
    return ConversationsMergeCheckErrorsEnum.UNSUPPORTED_CHANNEL;
  }

  return ConversationsMergeCheckErrorsEnum.NONE;
}

export enum ConversationsMergeCheckWarningsEnum {
  DIFFERENT_SENDERS = 'DIFFERENT_SENDERS',
  NONE = 'NONE',
}

export function computeConversationsMergeWarning(
  _conversations: ReadonlyArray<ConversationBaseModel>,
  conversationsRecipients: ReadonlyArray<VisualRecipientModel | undefined>,
) {
  // We want to test equality between contacts that don't have a card.
  const uniqueContacts = chain(conversationsRecipients)
    .filter((recipient) => !recipient?.card)
    .map((recipient) => recipient?.contact)
    .filter(isDefined)
    .uniqBy('uid')
    .value();

  // Or equality between cards for contacts that have one.
  const uniqueCards = chain(conversationsRecipients)
    .map((recipient) => recipient?.card)
    .filter(isDefined)
    .uniqBy((card) => card.id)
    .value();

  return uniqueContacts.length > 1 || uniqueCards.length > 1
    ? ConversationsMergeCheckWarningsEnum.DIFFERENT_SENDERS
    : ConversationsMergeCheckWarningsEnum.NONE;
}

/*
 * Sort order.
 */

export type ConversationDateGetter = (c: ConversationBaseModel) => number;
export function makeConversationDateGetter(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
): ConversationDateGetter {
  const {sortProperty} = parameters;

  if (sortProperty === ConversationSortPropertiesEnum.PRIORITY) {
    return (conversation) => conversation.orders[ConversationSortPropertiesEnum.WAITING_SINCE];
  }

  // By reminders.
  if (sortProperty === ConversationSortPropertiesEnum.EXPIRES_AT) {
    return (c) => {
      const reminder = findConversationReminder(cellsContext, c);
      if (!reminder) {
        return 0;
      }

      return reminder.remindAt;
    };
  }

  if (parameters.search) {
    return (c) => c.orders[sortProperty];
  }

  // By tracker.
  if (findParametersCellsSortMode(parameters) === CellsSortModesEnum.TRACKER) {
    return (c) => {
      const tracker = c.trackers[cellsContext.teammateId];

      if (!tracker) {
        const summary = {
          // Please contact security if you plan to log additional conversation details to bugsnag.
          id: c.id,
          namespace: c.namespace,
          teamStatus: c.teamStatus,
          trackers: c.trackers,
        };
        reportToBugsnag({
          name: 'Tracker not found',
          message: `Tracker ${cellsContext.teammateId} not found in ${JSON.stringify(summary)}`,
        });

        // TODO: Should we return undefined here? There is nothing that guarantees the conversation has a
        // tracker for the given user, so we should decide what to do downstream in this situation.
        return 0;
      }

      return tracker.bumpedAt;
    };
  }

  // By order.
  return (c) => c.orders[sortProperty];
}

export type ConversationPriorityDateGetter = (c: ConversationBaseModel) => number | undefined;
export function makeConversationPriorityDateGetter(
  cellsContext: CellsContext,
  parameters: InboxesConversationsParameters,
): ConversationPriorityDateGetter {
  const {sortProperty} = parameters;

  if (sortProperty !== ConversationSortPropertiesEnum.PRIORITY) {
    return () => undefined;
  }

  return (conversation) => {
    if (cellsContext.activeSlaRuleBundles === undefined) {
      reportToBugsnag(new Error('SLA Rule Bundles not loaded in Priority mode'));

      return Object.values(conversation.slaTimers).reduce<number>(
        (acc, s) => (s.breach_time < acc ? s.breach_time : acc),
        Infinity,
      );
    }

    return getEarliestSlaBreach(conversation, cellsContext.activeSlaRuleBundles) ?? Infinity;
  };
}

/*
 * Move.
 */

export function canMoveToInbox(inbox: InboxModel, conversations: ReadonlyArray<ConversationBaseModel>) {
  return conversations.every((conversation) =>
    isConversationNamespaceAllowedInInbox(inbox, conversation.namespace),
  );
}

export function canMoveConversationIdsToInbox(
  conversationIds: ReadonlyArray<number>,
  inbox: InboxModel,
  findConversationNamespace: ConversationNamespaceFinder,
) {
  const conversationNamespaces = chain(conversationIds)
    .map((id) => findConversationNamespace(id))
    .compact()
    .value();

  return areConversationNamespacesAllowedInInbox(inbox, conversationNamespaces);
}

export function areConversationNamespacesAllowedInInbox(
  inbox: InboxModel,
  conversationNamespaces: ReadonlyArray<string>,
) {
  return conversationNamespaces.every((conversationNamespace) =>
    isConversationNamespaceAllowedInInbox(inbox, conversationNamespace),
  );
}

function isConversationNamespaceAllowedInInbox(inbox: InboxModel, conversationNamespace: string) {
  // A team conversation is not allowed in a teammate inbox.
  if (isTeamNamespace(conversationNamespace) && isTeammateNamespace(inbox.namespace)) {
    return false;
  }

  // Otherwise, it's allowed.
  return true;
}

/*
 * Trackers.
 */

export function sortTrackersForConversation(conversation: ConversationBaseModel) {
  return _(conversation.trackers)
    .filter(isLiveTracker)
    .compact()
    .sortBy(getTrackerStatusScore, (t) => t.autoSeenAt && -t.autoSeenAt)
    .value();
}

function getTrackerStatusScore(tracker: ConversationTrackerModel) {
  return tracker.status === ConversationTrackerStatusesEnum.INBOX ? 1 : 2;
}

export function findTrackerStatus(conversation: ConversationBaseModel, teammateId: number) {
  const tracker = conversation.trackers[teammateId];
  return tracker && tracker.status;
}

export function findTrackerStage(conversation: ConversationBaseModel, teammateId: number) {
  const tracker = conversation.trackers[teammateId];
  return tracker && tracker.stage;
}

export function findLiveTracker(
  conversation: ConversationBaseModel | undefined,
  teammateId: number,
): ConversationLiveTrackerModel | undefined {
  const tracker = conversation?.trackers[teammateId];
  return isLiveTracker(tracker) ? tracker : undefined;
}

export function computeHasLiveTracker(
  conversation: ConversationBaseModel | undefined,
  teammateId: number,
): boolean {
  return Boolean(findLiveTracker(conversation, teammateId));
}

export function findOrCreateTracker(
  conversation: ConversationBaseModel,
  teammateId: number,
): ConversationTrackerModel {
  // If we already have a tracker, return it.
  const tracker = conversation.trackers[teammateId];
  if (tracker) {
    return tracker;
  }

  // Otherwise, create one.
  return {
    teammateId,
    stage: ConversationTrackerStagesEnum.NONE,
    status: ConversationTrackerStatusesEnum.INBOX,
    extra: [],
    bumpedAt: conversation.orders[ConversationSortPropertiesEnum.BUMPED_AT],
    seenAt: undefined,
  };
}

export function conversationsTrackerBumpedAt(
  conversations: ReadonlyArray<ConversationBaseModel>,
  teammateId: number,
): Record<ConversationId, BumpedAtTimestamp | undefined> {
  return conversations.reduce<Record<ConversationId, BumpedAtTimestamp | undefined>>((acc, conversation) => {
    const tracker = findOrCreateTracker(conversation, teammateId);
    return {...acc, [conversation.id]: tracker.bumpedAt};
  }, {});
}

/*
 * Namespaces.
 */

export function findConversationNamespaces(conversations: ReadonlyArray<ConversationBaseModel>) {
  return _(conversations)
    .map((c) => c.namespace)
    .uniq()
    .value();
}

/*
 * Update conversation.
 */

export type ConversationUpdater = <T extends ConversationBaseModel | ConversationModel>(conversation: T) => T;
export function buildConversationUpdater(
  ...updaters: Array<ConversationUpdater | null | undefined | false | '' | 0>
): ConversationUpdater {
  return flow(compact(updaters));
}

export function setConversationSubject(subject: string | undefined): ConversationUpdater {
  return (conversation) => ({
    ...conversation,
    subject: subject || '',
  });
}

export function addConversationInbox(inboxId: number): ConversationUpdater {
  return (conversation) => ({
    ...conversation,
    inboxIds: uniq([...conversation.inboxIds, inboxId]),
  });
}

export function bumpConversationOnSend(
  preference: BumpStrategiesEnum,
  strategy: BumpStrategiesEnum,
  teammateId: number,
  forceBump?: boolean,
): ConversationUpdater {
  return buildConversationUpdater(
    maybeBumpConversationOrder(preference, strategy, forceBump),
    bumpConversationFollowers(teammateId),
    markConversationRead(teammateId),
  );
}

export function maybeBumpConversationOrder(
  preference: BumpStrategiesEnum,
  strategy: BumpStrategiesEnum,
  forceBump?: boolean,
): ConversationUpdater {
  // Check if the specified strategy is enabled.
  const isWildcardStrategy = preference === BumpStrategiesEnum.MESSAGE_AND_COMMENT;
  if (!isWildcardStrategy && preference !== strategy && !forceBump) {
    return identity;
  }

  // If so, bump the bumped_at sort order.
  const date = Date.now();
  return (conversation) => ({
    ...conversation,
    orders: {
      ...conversation.orders,
      [ConversationSortPropertiesEnum.BUMPED_AT]: date,
      [ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE]: date,
      [ConversationSortPropertiesEnum.LAST_MESSAGE_OR_COMMENT_DATE]: date,
    },
  });
}

function bumpConversationFollowers(exceptTeammateId: number): ConversationUpdater {
  return (conversation) => {
    const otherTrackerIds = _(conversation.trackers)
      .values()
      .compact()
      .filter((t) => t.stage === ConversationTrackerStagesEnum.SUBSCRIBER)
      .map((t) => t.teammateId)
      .without(exceptTeammateId)
      .value();

    const updaters = otherTrackerIds.map((trackerId) => bumpConversationTracker(trackerId));
    return buildConversationUpdater(...updaters)(conversation);
  };
}

export function makeConversationActionContext(
  cellsContext: CellsContext,
  conversation: ConversationBaseModel,
): ConversationActionContextsEnum {
  if (isConversationInAssigneeContext(cellsContext, conversation)) {
    return ConversationActionContextsEnum.ASSIGNEE;
  }

  if (isConversationInTeamContext(cellsContext, conversation)) {
    return ConversationActionContextsEnum.TEAM;
  }

  return ConversationActionContextsEnum.TEAMMATE;
}

type ConversationPropFinder<T> = (conversationId: ConversationId) => T | undefined;
export type ConversationActionContextFinder = ConversationPropFinder<ConversationActionContextsEnum>;
export type ConversationNamespaceFinder = ConversationPropFinder<string>;
export type ConversationInboxIdsFinder = ConversationPropFinder<ReadonlyArray<number>>;
export type ConversationSubjectFinder = ConversationPropFinder<string>;
export type ConversationMessageTypeFinder = ConversationPropFinder<MessageTypesEnum>;
export type BumpedAtFinder = ConversationPropFinder<BumpedAtTimestamp>;

export function makeFindConversationProp<K extends keyof ConversationBaseModel>(
  conversations: ReadonlyArray<ConversationBaseModel>,
  prop: K,
): ConversationPropFinder<ConversationBaseModel[K]> {
  const conversationsAsMap = keyBy(conversations, (c) => c.id);

  return (id: ConversationId) => conversationsAsMap[id]?.[prop];
}

export function makeFindConversationActionContext(
  cellsContext: CellsContext,
  conversations: ReadonlyArray<ConversationBaseModel>,
) {
  const conversationsAsMap = keyBy(conversations, (c) => c.id);

  return (id: ConversationId) =>
    conversationsAsMap[id] && makeConversationActionContext(cellsContext, conversationsAsMap[id]);
}

export function makeFindConversationNamespace(conversations: ReadonlyArray<ConversationBaseModel>) {
  return makeFindConversationProp(conversations, 'namespace');
}

export function makeFindConversationInboxIds(conversations: ReadonlyArray<ConversationBaseModel>) {
  return makeFindConversationProp(conversations, 'inboxIds');
}

export function makeFindConversationSubject(conversations: ReadonlyArray<ConversationBaseModel>) {
  return makeFindConversationProp(conversations, 'subject');
}

export function makeFindConversationMessageType(conversations: ReadonlyArray<ConversationBaseModel>) {
  return makeFindConversationProp(conversations, 'messageType');
}

interface BumpConversationOptions {
  findBumpedAt?: BumpedAtFinder;
}

export function bumpConversationTracker(
  teammateId: number,
  options: BumpConversationOptions = {},
): ConversationUpdater {
  const {findBumpedAt} = options;

  return (conversation) => {
    const tracker = findOrCreateTracker(conversation, teammateId);

    const newTracker = {
      ...tracker,
      bumpedAt: (findBumpedAt && findBumpedAt(conversation.id)) || Date.now(),
    };

    const newTrackers = {
      ...conversation.trackers,
      [teammateId]: newTracker,
    };

    return {
      ...conversation,
      trackers: newTrackers,
    };
  };
}

export function maybeMakeConversationVisible(): ConversationUpdater {
  return (conversation) => {
    if (conversation.teamStatus !== ConversationTeamStatusesEnum.INVISIBLE) {
      return conversation;
    }

    return setConversationTeamStatus(ConversationTeamStatusesEnum.OPEN)(conversation);
  };
}

export function setConversationStatus(
  cellsContext: CellsContext,
  newStatus: ConversationStatusesEnum,
  findBumpedAt?: BumpedAtFinder,
): ConversationUpdater {
  const trackerStatus = statusToTrackerStatus(newStatus);
  const teamStatus = statusToTeamStatus(newStatus);

  // If the new status is "Destroyed", update the team status.
  if (newStatus === ConversationStatusesEnum.DESTROYED) {
    return setConversationTeamStatus(teamStatus);
  }

  return (conversation) => {
    const bumpedAt = findBumpedAt && findBumpedAt(conversation.id);

    // If our teammate is the assignee, or we're in a team context update both.
    // TODO: Update to match `buildSetStatusConversationPatch`, and only update team status in team context.
    if (isConversationInTeamOrAssigneeContext(cellsContext, conversation)) {
      return buildConversationUpdater(
        setConversationTeamStatus(teamStatus),
        setConversationTrackerStatus(cellsContext.teammateId, trackerStatus, bumpedAt),
      )(conversation);
    }

    // Otherwise, teammate status only.
    return setConversationTrackerStatus(cellsContext.teammateId, trackerStatus, bumpedAt)(conversation);
  };
}

export function setConversationTeamStatus(newTeamStatus: ConversationTeamStatusesEnum): ConversationUpdater {
  // Set the status + clear the last action.
  return (conversation) => ({
    ...conversation,
    teamStatus: newTeamStatus,
  });
}

export function setConversationTrackerStatus(
  teammateId: number,
  newStatus: ConversationTrackerStatusesEnum,
  bumpedAt: BumpedAtTimestamp | undefined,
): ConversationUpdater {
  return (conversation) => {
    const tracker = findOrCreateTracker(conversation, teammateId);
    const newTracker: ConversationTrackerModel = {
      ...tracker,
      status: newStatus,
      bumpedAt: bumpedAt || tracker.bumpedAt,
    };
    const newTrackers = {...conversation.trackers, [teammateId]: newTracker};

    return {
      ...conversation,
      trackers: newTrackers,
    };
  };
}

export function setConversationTrackerStage(
  teammateId: number,
  newStage: ConversationTrackerStagesEnum,
  bumpedAt: BumpedAtTimestamp | undefined,
): ConversationUpdater {
  return (conversation) => {
    const tracker = findOrCreateTracker(conversation, teammateId);
    const newTracker: ConversationTrackerModel = {
      ...tracker,
      stage: newStage,
      bumpedAt: bumpedAt || tracker.bumpedAt,
    };
    const newTrackers = {...conversation.trackers, [teammateId]: newTracker};

    return {
      ...conversation,
      trackers: newTrackers,
    };
  };
}

export function findConversationsReminder(
  cellsContext: CellsContext,
  conversations: ReadonlyArray<ConversationBaseModel>,
) {
  const reminders = _(conversations)
    .map((c) => findConversationReminder(cellsContext, c))
    .compact();

  if (reminders.value().length < conversations.length) {
    return undefined;
  }

  const uniqueReminders = reminders
    .uniqBy((r) => r.remindAt)
    .sortBy((r) => r.isTeamReminder)
    .value();

  return uniqueReminders.length === 1 ? uniqueReminders[0] : undefined;
}

export function findConversationsCanceledReminder(
  cellsContext: CellsContext,
  conversations: ReadonlyArray<ConversationBaseModel>,
) {
  const reminders = _(conversations)
    .map((c) => findConversationReminder(cellsContext, c, true))
    .compact()
    .value();

  if (reminders.length < conversations.length) {
    return undefined;
  }

  const uniqueReminders = _(reminders)
    .uniqBy((r) => r.remindAt)
    .sortBy((r) => r.isTeamReminder)
    .value();

  return uniqueReminders.length === 1 ? reminders[0] : undefined;
}

export function findConversationReminder(
  cellsContext: CellsContext,
  conversation: ConversationBaseModel,
  isCanceled = false,
) {
  const {teammateId} = cellsContext;
  const reminders = isCanceled ? conversation.canceledReminders : conversation.reminders;

  return reminders.find((reminder) => {
    // If this conversation is in a team context, return team reminders.
    if (isConversationInTeamContext(cellsContext, conversation)) {
      return reminder.isTeamReminder;
    }

    // Otherwise, return our reminders.
    return !reminder.isTeamReminder && reminder.teammateId === teammateId;
  });
}

export function setConversationReminder(
  cellsContext: CellsContext,
  remindAt: number | null | undefined,
): ConversationUpdater {
  if (remindAt === undefined) {
    return identity;
  }

  return (conversation) => {
    const {teammateId} = cellsContext;

    // If we're the assignee, apply to both team & tracker.
    if (isConversationInAssigneeContext(cellsContext, conversation)) {
      return buildConversationUpdater(
        setConversationTeamReminder(teammateId, remindAt),
        setConversationTrackerReminder(teammateId, remindAt),
      )(conversation);
    }

    // Otherwise, it depends on wether we're in a team context or not.
    return isConversationInTeamContext(cellsContext, conversation)
      ? setConversationTeamReminder(teammateId, remindAt)(conversation)
      : setConversationTrackerReminder(teammateId, remindAt)(conversation);
  };
}

export function setConversationTeamReminder(
  teammateId: number,
  remindAt: number | null,
): ConversationUpdater {
  return (conversation) => {
    const otherReminders = conversation.reminders.filter((r) => !r.isTeamReminder);

    // If no reminder is provided, remove all team reminders.
    if (!remindAt) {
      return {
        ...conversation,
        reminders: otherReminders,
      };
    }

    // Otherwise, create a new reminder and insert it.
    const newReminder = {
      teammateId,
      remindAt,
      isTeamReminder: true,
    };

    return {
      ...conversation,
      reminders: [...otherReminders, newReminder],
    };
  };
}

export function setConversationTrackerReminder(
  teammateId: number,
  remindAt: number | null,
): ConversationUpdater {
  return (conversation) => {
    const otherReminders = conversation.reminders.filter(
      (r) => r.isTeamReminder || r.teammateId !== teammateId,
    );

    // If no reminder is provided, remove all teammate reminders for our teammate.
    if (!remindAt) {
      return {
        ...conversation,
        reminders: otherReminders,
      };
    }

    // Otherwise, create a new reminder and insert it.
    const newReminder = {
      teammateId,
      remindAt,
      isTeamReminder: false,
    };

    return {
      ...conversation,
      reminders: [...otherReminders, newReminder],
    };
  };
}

export function shouldAutoAssignChannel(channel: ChannelModel): boolean {
  return channel.preferences.shouldAutoAssign;
}

export function shouldAutoAssignConversation(
  conversation: Pick<ConversationBaseModel, 'namespace' | 'assigneeId'>,
): boolean {
  const isAlreadyAssigned = Boolean(conversation.assigneeId);
  const isTeamConversation = isTeamNamespace(conversation.namespace);

  return !isAlreadyAssigned && isTeamConversation;
}

export function autoAssignConversation(userId: number): ConversationUpdater {
  return (conversation) => {
    if (!shouldAutoAssignConversation(conversation)) {
      return conversation;
    }

    return {...conversation, assigneeId: userId};
  };
}

export function autoSubscribeToConversation(userId: number): ConversationUpdater {
  return (conversation) => {
    // If our user is already subscribed, nothing to do.
    const userTracker = findOrCreateTracker(conversation, userId);
    if (
      userTracker.stage === ConversationTrackerStagesEnum.SUBSCRIBER ||
      userTracker.stage === ConversationTrackerStagesEnum.ASSIGNEE
    ) {
      return conversation;
    }

    // Otherwise, update the tracker and the conversation.
    const newUserTracker = {
      ...userTracker,
      stage: ConversationTrackerStagesEnum.SUBSCRIBER,
      seenAt: Date.now(),
    };

    const newTrackers = {
      ...conversation.trackers,
      [userId]: newUserTracker,
    };

    return {
      ...conversation,
      trackers: newTrackers,
    };
  };
}

export interface UpdateConversationTrackersOptions {
  add?: ReadonlyArray<number>;
  remove?: ReadonlyArray<number>;
  stage?: ConversationTrackerStagesEnum;
}

export function updateConversationTrackers(options: UpdateConversationTrackersOptions): ConversationUpdater {
  return (conversation) => {
    const newTrackerTrackers = _(options.add)
      .map((teammateId) => {
        // If there's already a tracker, check if we need to change it.
        const tracker = findOrCreateTracker(conversation, teammateId);
        if (isLiveTracker(tracker)) {
          return null;
        }

        // If so, create a new tracker.
        return {
          ...tracker,
          stage: options.stage || ConversationTrackerStagesEnum.TRACKER,
        };
      })
      .compact()
      .value();

    const newNoneTrackers = map(options.remove, (teammateId) => ({
      ...findOrCreateTracker(conversation, teammateId),
      stage: ConversationTrackerStagesEnum.NONE,
    }));

    const trackers = {
      ...conversation.trackers,
      ...keyBy(newTrackerTrackers, (t) => t.teammateId),
      ...keyBy(newNoneTrackers, (t) => t.teammateId),
    };

    return {
      ...conversation,
      trackers,
    };
  };
}

interface UpdateConversationTrackerOfflineOptions {
  teammateId: number;
  lastBumpedAt?: number;
  offlineBumpedId: string;
  stage?: ConversationTrackerStagesEnum;
  status?: ConversationTrackerStatusesEnum;
  bumpedAt?: number;
  // A number to set a reminder, null to clear a reminder, undefined to leave reminders unchanged.
  remindAt?: number | null;
}

export function updateConversationTrackerOffline(
  options: UpdateConversationTrackerOfflineOptions,
): ConversationUpdater {
  const {teammateId, lastBumpedAt, offlineBumpedId, stage, status, bumpedAt, remindAt} = options;

  const maybeReminderUpdater: ConversationUpdater = !isUndefined(remindAt)
    ? setConversationTrackerReminder(teammateId, remindAt)
    : identity;

  return (conversation) => {
    // If the user is not already a tracker, ignore.
    const tracker = conversation.trackers[teammateId];
    if (!tracker) {
      return conversation;
    }

    // If this action conflicts, ignore.
    const isConflictingUpdate =
      !isUndefined(lastBumpedAt) &&
      lastBumpedAt !== tracker.bumpedAt &&
      offlineBumpedId !== tracker.offlineBumpedId;
    if (isConflictingUpdate) {
      return conversation;
    }

    const newTracker: ConversationTrackerModel = {
      ...tracker,
      stage: !isUndefined(stage) ? stage : tracker.stage,
      status: !isUndefined(status) ? status : tracker.status,
      bumpedAt: !isUndefined(bumpedAt) ? bumpedAt : tracker.bumpedAt,
      offlineBumpedId,
    };

    return maybeReminderUpdater({
      ...conversation,
      trackers: {
        ...conversation.trackers,
        [teammateId]: newTracker,
      },
    });
  };
}

/*
 * Tags.
 */

export interface UpdateConversationTagsOptions {
  add?: ReadonlyArray<number>;
  remove?: ReadonlyArray<number>;
}

export function updateConversationTags(options: UpdateConversationTagsOptions): ConversationUpdater {
  return (conversation) => {
    const newTagIds = _(conversation.tagIds)
      .concat(options.add || [])
      .difference(options.remove || [])
      .uniq()
      .value();

    return {...conversation, tagIds: newTagIds};
  };
}

/*
 * Last message/comment.
 */

export function mergeLastComment(
  existingLastComment: ConversationLastComment | undefined,
  newLastComment: ConversationLastComment | undefined,
) {
  // The last comment didn't change, preserve identity.
  if (isEqual(existingLastComment, newLastComment)) {
    return existingLastComment;
  }

  // No previous last comment, use the new one.
  if (!existingLastComment) {
    return newLastComment;
  }

  // If the new last comment isn't a partial variant of the previous, use it.
  if (
    !newLastComment ||
    newLastComment.uid === existingLastComment.uid ||
    !newLastComment.uid.endsWith(existingLastComment.uid)
  ) {
    return newLastComment;
  }

  // Otherwise, use the old one.
  return existingLastComment;
}

/**
 * Can be used to merge conversation.lastMessage with a new message.
 * Can only be used to merge conversation.lastManualMessage if it is already known that the provided newMessage is not an auto-response.
 * If it may be an auto-response, use {@see mergeLastManualMessage} instead.
 */
export function mergeLastMessage(
  existingLastMessage: ConversationLastMessage | undefined,
  newMessage: ConversationLastMessage | undefined,
) {
  // The last message didn't change, preserve identity.
  if (isEqual(existingLastMessage, newMessage)) {
    return existingLastMessage;
  }

  // No previous last message, use the new one.
  if (!existingLastMessage) {
    return newMessage;
  }

  // If the new message is older than the existing one, keep the existing one.
  if (existingLastMessage && newMessage && existingLastMessage.sentAt > newMessage.sentAt) {
    return existingLastMessage;
  }

  // If the new message isn't a partial variant of the previous, use it.
  if (
    !newMessage ||
    newMessage.uid === existingLastMessage.uid ||
    !newMessage.uid.endsWith(existingLastMessage.uid)
  ) {
    return newMessage;
  }

  // Otherwise, use the old one.
  return existingLastMessage;
}

/**
 * Can be used to merge conversation.lastManualMessage when it is not known if the provided newMessage is an auto-response.
 * If it definitely is not an auto-response, you may be able to use {@see mergeLastMessage} instead, which doesn't require having the newMessageActivity provided.
 */
export function mergeLastManualMessage(
  existingLastManualMessage: ConversationLastMessage | undefined,
  newMessage: ConversationLastMessage | undefined,
  newMessageActivity: ActivityModel | undefined,
) {
  // Only consider using the new message if both the message and the activity are present.
  if (!newMessage || !newMessageActivity) {
    return existingLastManualMessage;
  }

  const newMessageActivitySourceVisualType = newMessageActivity
    ? computeMessageActivitySourceVisualType(newMessageActivity)
    : undefined;
  const isNewMessageAutoResponse = computeIsAutoResponseMessageActivitySource(
    newMessageActivitySourceVisualType,
  );
  if (isNewMessageAutoResponse) {
    return existingLastManualMessage;
  }

  // We now know the new message is a manual message (not an auto-response).
  return mergeLastMessage(existingLastManualMessage, newMessage);
}

export function buildConversationMessagePartial(message: MessageModel): ConversationLastMessage {
  return {
    uid: message.uid,
    sentAt: message.sentAt,
    status: message.status,
  };
}

/*
 * Context.
 */

export function isConversationInTeamContext(
  {type}: CellsContext,
  conversation: Pick<ConversationBaseModel, 'namespace' | 'trackers'>,
): boolean {
  const isTeamConversation = isTeamNamespace(conversation.namespace);
  switch (type) {
    // If we're in a team mixed context, only rely on the conversation.
    case CellsContextTypesEnum.MIXED_TEAM_TAG:
    case CellsContextTypesEnum.CUSTOM_VIEW:
    case CellsContextTypesEnum.MIXED_SEARCH_GLOBAL:
      return isTeamConversation;

    // If we're in a normal context, check if it's a team one.
    default:
      return type === CellsContextTypesEnum.TEAM;
  }
}

export function isConversationInTeamOrAssigneeContext(
  cellsContext: CellsContext,
  conversation: ConversationBaseModel,
): boolean {
  return (
    isConversationInTeamContext(cellsContext, conversation) ||
    isConversationInAssigneeContext(cellsContext, conversation)
  );
}

export function findConversationContextNamespace(
  cellsContext: CellsContext,
  conversation: ConversationBaseModel,
): string {
  // If the conversation is in a team context or the user is the conversation assignee, use its namespace.
  if (isConversationInTeamOrAssigneeContext(cellsContext, conversation)) {
    return conversation.namespace;
  }

  // Otherwise, use the namespace of the teammate we're acting as.
  return buildTeammateNamespace(cellsContext.teammateId);
}

export function isConversationInAssigneeContext(
  cellsContext: CellsContext,
  conversation: ConversationBaseModel,
): boolean {
  return (
    // Must be a team conversation.
    isTeamNamespace(conversation.namespace) &&
    // Must be the assignee.
    conversation.assigneeId === cellsContext.teammateId &&
    // Must either be working from a team context, or the assignee's tracker status must be in sync.
    (isConversationInTeamContext(cellsContext, conversation) ||
      computeIsTrackerStatusInSync(cellsContext, conversation))
  );
}

function computeIsTrackerStatusInSync(
  {teammateId}: CellsContext,
  conversation: ConversationBaseModel,
): boolean {
  const trackerStatus = findLiveTracker(conversation, teammateId)?.status;

  // If the conversation bumped only for the tracker (and not for the team), the tracker is out of sync.
  if (
    trackerStatus === ConversationTrackerStatusesEnum.INBOX &&
    conversation.teamStatus !== ConversationTeamStatusesEnum.OPEN
  ) {
    return false;
  }

  return true;
}

/*
 * Unread status.
 */

export enum UnreadStatusesEnum {
  NONE = 'NONE',
  MESSAGE_UNREAD = 'MESSAGE_UNREAD',
  SNOOZE_UNREAD = 'SNOOZE_UNREAD',
}

export function areConversationsUnread(
  cellsContext: CellsContext,
  conversations: ReadonlyArray<ConversationBaseModel>,
) {
  return conversations.every((c) => findUnreadStatus(cellsContext, c) !== UnreadStatusesEnum.NONE);
}

export function findUnreadStatus(cellsContext: CellsContext, conversation: ConversationBaseModel) {
  const {teammateId} = cellsContext;
  const isInTeamContext = isConversationInTeamContext(cellsContext, conversation);

  // Tracker unsnoozed.
  const tracker = conversation.trackers[teammateId];
  const isTrackerSnoozeUnread = isTrackerUnsnoozed(conversation, tracker);

  // Team unsnoozed.
  const conversationBumpedAt = conversation.orders[ConversationSortPropertiesEnum.BUMPED_AT];
  const isTeamSnoozeUnread =
    conversation.isUnsnoozed && (!conversation.seenAt || conversationBumpedAt > conversation.seenAt);

  // Message unread.
  const lastActivityDate = findLastActivityDate(conversation, teammateId);
  const isMessageUnread = !conversation.seenAt || lastActivityDate > conversation.seenAt;

  return pickUnreadStatus(isInTeamContext, isTrackerSnoozeUnread, isTeamSnoozeUnread, isMessageUnread);
}

export function pickUnreadStatus(
  isInTeamContext: boolean,
  isTrackerSnoozeUnread: boolean,
  isTeamSnoozeUnread: boolean,
  isMessageUnread: boolean,
) {
  // Tracker unsnoozed.
  if (!isInTeamContext && isTrackerSnoozeUnread) {
    return UnreadStatusesEnum.SNOOZE_UNREAD;
  }

  // Team unsnoozed.
  if (isInTeamContext && isTeamSnoozeUnread) {
    return UnreadStatusesEnum.SNOOZE_UNREAD;
  }

  // Message unread.
  if (isMessageUnread) {
    return UnreadStatusesEnum.MESSAGE_UNREAD;
  }

  return UnreadStatusesEnum.NONE;
}

export function findLastActivityDate(conversation: ConversationBaseModel, teammateId: number) {
  const {trackers, orders} = conversation;
  const tracker = trackers[teammateId];

  const trackerBumpedAt = tracker && tracker.bumpedAt;
  const lastActivityDate = orders[ConversationSortPropertiesEnum.LAST_ACTIVITY_DATE];

  return Math.max(trackerBumpedAt || 0, lastActivityDate);
}

export function markConversationRead(teammateId: number): ConversationUpdater {
  return (conversation) => {
    const lastActivityDate = findLastActivityDate(conversation, teammateId);
    const newSeenAt = lastActivityDate + 1;

    const tracker = findOrCreateTracker(conversation, teammateId);
    const newTracker = {
      ...tracker,
      seenAt: newSeenAt,
    };

    const newTrackers = {
      ...conversation.trackers,
      [teammateId]: newTracker,
    };

    return {
      ...conversation,
      trackers: newTrackers,
      seenAt: newSeenAt,
    };
  };
}

export function findConversationSeenAtEverything(conversation: ConversationBaseModel, teammateId: number) {
  // Tracker unsnoozed date.
  const tracker = conversation.trackers[teammateId];
  const trackerUnsnoozedDate = findTrackerUnsnoozedDate(tracker) || 0;

  // Team unsnoozed date.
  const conversationBumpedAt = conversation.orders[ConversationSortPropertiesEnum.BUMPED_AT];
  const conversationUnsnoozedDate = (conversation.isUnsnoozed && conversationBumpedAt) || 0;

  // Last activity date.
  const lastActivityDate = findLastActivityDate(conversation, teammateId);

  return Math.max(trackerUnsnoozedDate, conversationUnsnoozedDate, lastActivityDate);
}

export function findConversationNewReadDate(conversation: ConversationBaseModel, teammateId: number) {
  const unreadDate = findConversationSeenAtEverything(conversation, teammateId);

  // TODO: Why are we adding 1000 here?
  return Math.max(unreadDate + 1000, Date.now());
}

/*
 * Status.
 */

export enum ConversationStatusesEnum {
  OPEN = 'OPEN',
  ARCHIVED = 'ARCHIVED',
  TRASHED = 'TRASHED',
  SPAM = 'SPAM',
  DESTROYED = 'DESTROYED',
}

export function findConversationStatus(cellsContext: CellsContext, conversation: ConversationBaseModel) {
  const {teammateId} = cellsContext;
  const tracker = findLiveTracker(conversation, teammateId);

  // If we're in a team context, use the team status.
  if (isConversationInTeamContext(cellsContext, conversation) || !tracker) {
    return teamStatusToStatus(conversation.teamStatus);
  }

  // Otherwise, use the teammate status.
  return trackerStatusToStatus(tracker.status);
}

type StatusToTeamStatusMap = {[K in ConversationStatusesEnum]: ConversationTeamStatusesEnum};
const statusToTeamStatusMap: StatusToTeamStatusMap = {
  [ConversationStatusesEnum.OPEN]: ConversationTeamStatusesEnum.OPEN,
  [ConversationStatusesEnum.ARCHIVED]: ConversationTeamStatusesEnum.ARCHIVED,
  [ConversationStatusesEnum.TRASHED]: ConversationTeamStatusesEnum.TRASHED,
  [ConversationStatusesEnum.SPAM]: ConversationTeamStatusesEnum.SPAM,
  [ConversationStatusesEnum.DESTROYED]: ConversationTeamStatusesEnum.DESTROYED,
};
export function statusToTeamStatus(status: ConversationStatusesEnum) {
  return statusToTeamStatusMap[status];
}

type TeamStatusToStatusMap = {[K in ConversationTeamStatusesEnum]?: ConversationStatusesEnum};
const teamStatusToStatusMap: TeamStatusToStatusMap = invert(statusToTeamStatusMap);
export function teamStatusToStatus(status: ConversationTeamStatusesEnum) {
  return teamStatusToStatusMap[status] || ConversationStatusesEnum.OPEN;
}

type StatusToTrackerStatusMap = {[K in ConversationStatusesEnum]?: ConversationTrackerStatusesEnum};
const statusToTrackerStatusMap: StatusToTrackerStatusMap = {
  [ConversationStatusesEnum.OPEN]: ConversationTrackerStatusesEnum.INBOX,
  [ConversationStatusesEnum.ARCHIVED]: ConversationTrackerStatusesEnum.ARCHIVED,
  [ConversationStatusesEnum.TRASHED]: ConversationTrackerStatusesEnum.TRASHED,
  [ConversationStatusesEnum.SPAM]: ConversationTrackerStatusesEnum.SPAM,
};
export function statusToTrackerStatus(status: ConversationStatusesEnum) {
  return statusToTrackerStatusMap[status] || ConversationTrackerStatusesEnum.INBOX;
}

type TrackerStatusToStatusMap = {[K in ConversationTrackerStatusesEnum]?: ConversationStatusesEnum};
const trackerStatusToStatusMap: TrackerStatusToStatusMap = invert(statusToTrackerStatusMap);
export function trackerStatusToStatus(status: ConversationTrackerStatusesEnum) {
  return trackerStatusToStatusMap[status] || ConversationStatusesEnum.OPEN;
}

/*
 * Content updated.
 */

export function findContentUpdatedAt(contentUpdatedDates: ContentUpdatedDates, userId: number): number {
  // If the current user is a draft author, use that date, otherwise fallback to the default timestamp.
  return contentUpdatedDates[userId] || contentUpdatedDates.default;
}

/** Checks if an activity is a type we want to scroll to in the conversation timeline. */
export function isActivityInConversationRelevant(activity: ActivityModel) {
  return (
    activity.visualType === ActivityVisualTypesEnum.MESSAGE ||
    activity.visualType === ActivityVisualTypesEnum.COMMENT ||
    activity.type === BasicActivityTypesEnum.SENDING_ERROR ||
    activity.type === BasicActivityTypesEnum.MESSAGE_BOUNCE_ERROR
  );
}

export function isCurrentUserAuthor(activity: ActivityModel, userId: number) {
  if (
    activity.visualType !== ActivityVisualTypesEnum.MESSAGE &&
    activity.visualType !== ActivityVisualTypesEnum.COMMENT
  ) {
    return false;
  }

  return activity.authorId && activity.authorId === userId;
}

/** Checks if an activity is an unread comment that at mentions the specified user. */
export function isActivityInConversationUnreadMention(
  activity: ActivityModel,
  userAlias: string,
  seenAt: number | undefined,
) {
  return (
    activity.visualType === ActivityVisualTypesEnum.COMMENT &&
    (!seenAt || activity.date > seenAt) &&
    activity.mentionedAliases.includes(userAlias)
  );
}

/** Checks if an activity is an unseen rule notification. */
export function isActivityInConversationRuleNotification(
  activity: ActivityModel,
  seenAt: number | undefined,
) {
  return (!seenAt || activity.date > seenAt) && activity.type === BasicActivityTypesEnum.RULE_NOTIFY;
}

/*
 * Draft messages.
 */

export function selectDraftMessageUidsForConversation(
  conversation: ConversationModel,
  offlineDraftMessagesByConversationId: Record<number, ReadonlyArray<MessageModel>>,
) {
  const {draftMessageUids, unsavedDraftMessageUids = []} = conversation;

  // We have to combine offline and online drafts, because offline drafts may be loaded into the state before the
  // conversation and therefore not show up in the conversation's unsavedDraftMessageUids.
  const offlineDraftMessages = offlineDraftMessagesByConversationId[conversation.id] || [];
  const offlineDraftMessageUids = map(offlineDraftMessages, (d) => d.uid);

  return union(draftMessageUids, unsavedDraftMessageUids, offlineDraftMessageUids);
}

// Check if a conversation has a draft set to draft later
export function computeIsAnyConversationDraftLater(conversation: ReadonlyArray<ConversationBaseModel>) {
  return conversation
    .filter((c): c is ConversationModel => 'lastDraft' in c)
    .some((c) => c.lastDraft?.status === MessageStatusesEnum.DRAFT_LATER);
}

/*
 * Offline conflict resolution.
 */

/** Takes the offline IDs from the server response and applies them to the conversation trackers if they match. */
export function applyTrackerBumpedOfflineId(
  teammateId: number,
  requestedOfflineId: string,
  dataGroup: DataGroupModel,
): DataGroupModel {
  const {conversations, acceptedOfflineIds} = dataGroup;
  if (!conversations) {
    return dataGroup;
  }

  const offlineBumpedId =
    acceptedOfflineIds && acceptedOfflineIds.includes(requestedOfflineId) ? requestedOfflineId : undefined;

  return {
    ...dataGroup,
    conversations: conversations.map((conversation) => {
      const {trackers} = conversation;
      const tracker = trackers[teammateId];
      if (!tracker) {
        return conversation;
      }

      return {
        ...conversation,
        trackers: {
          ...trackers,
          [teammateId]: {
            ...tracker,
            offlineBumpedId,
          },
        },
      };
    }),
  };
}

/*
 * Recent Conversation Helpers.
 */

export function splitConversationsIntoRecentAndOlder<T extends ConversationBaseModel>(
  conversations: ReadonlyArray<T>,
) {
  return partition(conversations, (conversation) => {
    const bumpedAt = DateTime.fromMillis(conversation.orders[ConversationSortPropertiesEnum.BUMPED_AT]);
    return computeIsWithinOneWeek(bumpedAt);
  });
}

const timeFormatsSmallerThanDay = [
  TimeDistanceFormatsEnum.NOW,
  TimeDistanceFormatsEnum.MINUTE,
  TimeDistanceFormatsEnum.HOUR,
];
function computeIsWithinOneWeek(datetime: DateTime) {
  const timeDistance = findRelativeTimeDistance(datetime.toMillis());

  // If the format is smaller, then it's guaranteed to be within based on the implementation of findDistance
  if (timeFormatsSmallerThanDay.includes(timeDistance.format)) {
    return true;
  }

  // If the formats are the same, then compare the values
  if (timeDistance.format === TimeDistanceFormatsEnum.DAY && timeDistance.value <= 7) {
    return true;
  }

  return false;
}

/*
 * Helper to determine whether the conversation is considered in a private tag.
 */
export function considerInPrivateTag(
  conversation: ConversationBaseModel,
  cellsContext: CellsContext,
  inboxesParameters: InboxesParameters,
): boolean {
  const {teammateInboxTagId, type, teammateId} = cellsContext;
  const inPersonalTag =
    type === CellsContextTypesEnum.TEAMMATE &&
    inboxesParameters.type === InboxesParametersTypesEnum.TEAMMATE_VIEW;
  const hasInboxTag = conversation.tagIds.includes(teammateInboxTagId);
  const isTeammateAssignee = teammateId === conversation.assigneeId;

  return inPersonalTag && !hasInboxTag && !isTeammateAssignee;
}
